<template>
  <CheckPermission
    :container-class="containerClass"
    :permission="permission"
  >
    <input
      ref="file"
      type="file"
      :accept="accept"
      class="d-none"
      @change="uploadFile($event)"
    />
    <b-button
      v-if="typeButton !== 'small'"
      :variant="color"
      :class="buttonClass"
      @click="$refs.file.click()"
      :disabled="progress.enabled || disabled"
    >
      <slot>{{ text }}</slot>
    </b-button>
    <b-button
      v-if="typeButton === 'small'"
      :variant="color"
      :class="buttonClass"
      @click="$refs.file.click()"
      :disabled="progress.enabled || disabled"
      size="sm"
    >
      <feather-icon
        v-b-tooltip.hover.left="'Adjuntar documento'"
        icon="PaperclipIcon" />
    </b-button>
    <vs-progress
      v-if="progress.enabled"
      :percent="progress.percent"
      :indeterminate="progress.indeterminate"
      :color="color"
    />
  </CheckPermission>
</template>
<script>

import { uuid } from 'vue-uuid';
import { BButton } from 'bootstrap-vue';

import CheckPermission from '@/febos/global/usuario/components/CheckPermission';
import { ioGetPrivateUploadUrl, ioUploadFileToPrivateUrl } from '@/febos/servicios/api/herramientas.api';

const paths = {
  ed: `febos-io/chile/escritorio-digital/${process.env.VUE_APP_AMBIENTE}/archivos/expedientes/`,
  saval: `febos-io/chile/saval-demo/${process.env.VUE_APP_AMBIENTE}/archivos/`
};

export default {
  data: () => ({
    progress: {
      enabled: false,
      percent: 0,
      indeterminate: true
    }
  }),
  methods: {
    getFileExtension(file) {
      const lastDot = file.name.lastIndexOf('.');
      if (lastDot === -1) {
        return '';
      }
      return file.name.substring(lastDot + 1);
    },
    async uploadFile(event) {
      const [file] = event.target.files;

      if (!file) {
        return false;
      }
      const fileExtension = this.getFileExtension(file);
      const isPdf = fileExtension.toLowerCase() === 'pdf' || file.type === 'application/pdf';
      if (isPdf && file.size > this.maxSizeInMb * 1048576) {
        this.$bvToast.toast(
          `El archivo ${file.name} supera el máximo de tamaño permitido por archivo (${this.maxSizeInMb} MB)`,
          {
            title: '¡Oye!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            noAutoHide: true
          }
        );
        this.$emit('input', {
          failed: true,
          failedMessage: `El archivo ${file.name} supera el máximo de tamaño permitido por archivo (${this.maxSizeInMb} MB)`
        });

        return false;
      }

      const fileId = uuid.v1();
      const baseUrl = paths[this.uploadTo] || this.uploadTo;
      const identifier = this.identifier ? `-${this.identifier}` : '';
      const url = `${baseUrl}${Date.now()}${fileId}${identifier}.${file.name.split('.').pop()}`;

      try {
        this.progress.enabled = true;
        this.progress.indeterminate = true;

        const response = await ioGetPrivateUploadUrl(url, file.type);

        this.progress.indeterminate = false;

        await ioUploadFileToPrivateUrl(
          response.data?.url,
          file,
          (percent) => {
            this.progress.percent = percent;
          }
        );

        let fileSha = null;
        try {
          fileSha = await this.getFileSHA(file);
        } catch (e) {
          console.error(e);
        }

        this.$emit('input', {
          id: fileId,
          name: file.name,
          currentDescription: file.name,
          mime: file.type,
          date: this.$moment().format('YYYY-MM-DD HH:MM:ss'),
          path: url,
          failed: false,
          ...(this.withSha && fileSha
            ? { sha: fileSha }
            : {}
          ),
        });
        this.$refs.file.value = '';
      } catch (e) {
        console.error(e);
        this.$emit('input', {
          failed: true,
          failedMessage: e.message
        });
      } finally {
        this.progress.enabled = false;
        this.progress.percent = 0;
        this.progress.indeterminate = false;
      }
      return true;
    },
    async getFileSHA(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            const arrayBuffer = event.target.result;
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hash = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
            resolve(hash);
          } catch (error) {
            reject(new Error('Error calculando el hash'));
          }
        };

        reader.onerror = () => {
          reject(new Error('Error leyendo el archivo'));
        };

        reader.readAsArrayBuffer(file);
      });
    }

  },
  props: {
    permission: {
      type: String,
      required: true
    },
    text: {
      type: String,
    },
    accept: {
      type: String
    },
    value: {
      type: Object
    },
    uploadTo: {
      type: String,
      required: true
    },
    maxSizeInMb: {
      type: Number,
      default: 20
    },
    identifier: {
      type: String
    },
    color: {
      type: String,
      default: 'primary'
    },
    buttonClass: {
      type: [String, Object]
    },
    containerClass: {
      type: [String, Object]
    },
    typeButton: {
      type: String
    },
    withSha: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CheckPermission,
    BButton
  }
};

</script>
